import moment from "moment";

export const getEventId = (event = {}) => (
  event?.id ??
  event?.Id ??
  event?.calendar_event_id ??
  event?.calendarEventId ??
  event?.event_id ??
  event?.eventId ??
  null
);

export const getEventTitle = (event = {}) => (
  event?.Subject ||
  event?.title ||
  event?.name ||
  event?.eventName ||
  event?.upcomingEventName ||
  "Untitled"
);

export const getCleanEventTitle = (event = {}) => {
  const rawTitle = String(getEventTitle(event)).trim();
  return rawTitle.replace(/^Reminder\s*[-:]\s*/i, "").trim() || rawTitle;
};

export const getCategory = (event = {}) => (
  String(event?.category || event?.Category || event?.categoryName || "event")
    .trim()
    .toLowerCase()
);

export const getEventOwnerId = (event = {}) => (
  event?.user_id ??
  event?.userId ??
  event?.UserId ??
  event?.created_by ??
  event?.createdBy ??
  event?.employee_id ??
  event?.employeeId ??
  null
);

export const parseTargetUserIds = (event = {}) => {
  const rawTargetUsers =
    event?.target_user_ids ||
    event?.TargetUserIds ||
    event?.targetUserIds ||
    [];

  if (Array.isArray(rawTargetUsers)) {
    return rawTargetUsers.map((id) => String(id));
  }

  if (typeof rawTargetUsers === "string") {
    try {
      const parsed = JSON.parse(rawTargetUsers);
      return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [];
    } catch (error) {
      return rawTargetUsers
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
    }
  }

  return [];
};

export const getShowToAll = (event = {}) => {
  const rawValue =
    event?.showToAll ??
    event?.show_to_all ??
    event?.ShowToAll ??
    event?.show_all;

  if (typeof rawValue === "boolean") {
    return rawValue;
  }

  if (typeof rawValue === "number") {
    return rawValue === 1;
  }

  if (typeof rawValue === "string") {
    const normalizedValue = rawValue.trim().toLowerCase();
    if (["1", "true", "yes"].includes(normalizedValue)) {
      return true;
    }
    if (["0", "false", "no"].includes(normalizedValue)) {
      return false;
    }
  }

  return false;
};

export const isMeetingEvent = (event = {}, category = getCategory(event)) => {
  if (category === "meeting") {
    return true;
  }

  if (["reminder", "calendar", "title", "event"].includes(category)) {
    const rawTitle = String(getEventTitle(event)).toLowerCase();
    return (
      rawTitle.startsWith("meeting") ||
      rawTitle.includes("meeting -") ||
      rawTitle.includes("reminder - meeting") ||
      rawTitle.includes(" scrum") ||
      rawTitle.startsWith("scrum")
    );
  }

  return false;
};

export const getEffectiveCategory = (event = {}) => {
  const category = getCategory(event);
  return isMeetingEvent(event, category) ? "meeting" : category;
};

export const getEventStartMoment = (event = {}, treatAsAllDay = false) => {
  const rawValue =
    event?.startDate ||
    event?.start_date ||
    event?.StartTime ||
    event?.startTime ||
    event?.start ||
    event?.date ||
    event?.eventdate ||
    event?.event_date ||
    "";

  if (!rawValue) {
    return moment.invalid();
  }

  const directMoment = moment(rawValue);
  if (directMoment.isValid()) {
    return treatAsAllDay ? directMoment.startOf("day") : directMoment;
  }

  return moment(rawValue, [
    "YYYY-MM-DD h:mm A",
    "YYYY-MM-DD hh:mm A",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
    "DD-MM-YYYY",
    "MM-DD-YYYY",
  ], true);
};

export const getLeaveOwnerName = (event = {}) => (
  event?.firstName ||
  event?.userName ||
  event?.employeeName ||
  event?.employee_name ||
  null
);

export const formatMeetingTitle = (event = {}) => {
  const cleanedTitle = getCleanEventTitle(event);
  if (/^meeting\s*[-:]/i.test(cleanedTitle)) {
    return cleanedTitle;
  }
  return `Meeting - ${cleanedTitle}`;
};

export const isMeetingRecipient = (event = {}, { isAdmin = false, currentUserId = null } = {}) => {
  if (isAdmin) {
    return true;
  }

  const showToAll = getShowToAll(event);
  if (showToAll) {
    return true;
  }

  const ownerId = getEventOwnerId(event);
  if (ownerId !== null && currentUserId !== null && String(ownerId) === String(currentUserId)) {
    return true;
  }

  const targetUserIds = parseTargetUserIds(event);
  return currentUserId !== null && targetUserIds.includes(String(currentUserId));
};

export const getLeaveStatus = (event = {}) => {
  const rawStatus = String(
    event?.LeaveStatus ||
    event?.leaveStatus ||
    event?.leave_status ||
    event?.Status ||
    event?.status ||
    event?.resource?.LeaveStatus ||
    event?.resource?.leaveStatus ||
    event?.resource?.Status ||
    event?.resource?.status ||
    ""
  ).toLowerCase().trim();

  const titleLower = String(getEventTitle(event)).toLowerCase();

  if (rawStatus.includes("pending") || titleLower.includes("pending")) {
    return "pending";
  }
  if (rawStatus.includes("declin") || titleLower.includes("declin")) {
    return "declined";
  }
  if (rawStatus.includes("unapprov") || titleLower.includes("unapprov")) {
    return "unapproved";
  }
  if (rawStatus.includes("cancel") || titleLower.includes("cancel")) {
    return "cancelled";
  }
  if (rawStatus.includes("approve") || titleLower.includes("approve")) {
    return "approved";
  }

  return "approved";
};

export const getStableEventKey = (event = {}) => {
  const rawId = getEventId(event);
  if (rawId !== null && typeof rawId !== "undefined") {
    const strId = String(rawId).trim();
    const match = strId.match(/^[a-z]+-(\d+)-/i);
    if (match) {
      return match[1];
    }
    return strId;
  }
  const cleanTitle = getCleanEventTitle(event).toLowerCase().replace(/[^a-z0-9]/g, "");
  const startMoment = getEventStartMoment(event, false);
  const startStr = startMoment.isValid() ? startMoment.format("YYYYMMDDHHmm") : "";
  return `${cleanTitle}_${startStr}`;
};
